import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../lib/utils';
import { scaleInItem } from '../../hooks/useAnimation';

type GlassCardVariant = 'default' | 'elevated' | 'glowing';

interface GlassCardProps {
  children: React.ReactNode;
  variant?: GlassCardVariant;
  className?: string;
  hover?: boolean;
  onClick?: () => void;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  delay?: number;
}

const variantStyles: Record<GlassCardVariant, string> = {
  default: 'backdrop-blur-xl bg-white/[0.04] border border-white/[0.08]',
  elevated: 'backdrop-blur-2xl bg-white/[0.07] border border-white/[0.1] shadow-card',
  glowing: 'backdrop-blur-xl bg-white/[0.04] border border-vokentra/30 shadow-glow-red',
};

const paddingStyles = {
  none: '',
  sm: 'p-3',
  md: 'p-5',
  lg: 'p-6',
};

export const GlassCard = React.forwardRef<HTMLDivElement, GlassCardProps>(
  function GlassCardInner({
    children,
    variant = 'default',
    className,
    hover = true,
    onClick,
    header,
    footer,
    padding = 'md',
    delay = 0,
  }, ref) {
    return (
      <motion.div
        ref={ref}
        variants={scaleInItem}
        initial="hidden"
        animate="visible"
        exit="exit"
        transition={{ delay }}
        whileHover={
          hover
            ? {
                scale: 1.01,
                y: -2,
                boxShadow: '0 8px 40px rgba(0, 0, 0, 0.35)',
                transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] },
              }
            : undefined
        }
        whileTap={onClick ? { scale: 0.99 } : undefined}
        onClick={onClick}
        className={cn(
          'rounded-2xl relative overflow-hidden transition-colors duration-300',
          variantStyles[variant],
          paddingStyles[padding],
          onClick && 'cursor-pointer',
          className
        )}
      >
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />

        {/* Glow effect for glowing variant */}
        {variant === 'glowing' && (
          <div className="absolute -inset-[1px] bg-gradient-to-r from-vokentra/20 via-transparent to-vokentra/10 rounded-2xl blur-xl opacity-50 pointer-events-none" />
        )}

        <div className="relative z-10">
          {header && (
            <div className="mb-4 flex items-center justify-between">
              {header}
            </div>
          )}
          {children}
          {footer && (
            <div className="mt-4 pt-4 border-t border-white/[0.06]">
              {footer}
            </div>
          )}
        </div>
      </motion.div>
    );
  }
);

// Compact KPI Card variant
interface KPICardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: React.ReactNode;
  color?: 'red' | 'green' | 'blue' | 'orange';
  delay?: number;
}

const colorMap = {
  red: 'from-vokentra/20 to-vokentra/5 text-vokentra',
  green: 'from-success/20 to-success/5 text-success',
  blue: 'from-info/20 to-info/5 text-info',
  orange: 'from-warning/20 to-warning/5 text-warning',
};

export function KPICard({ title, value, change, icon, color = 'red', delay = 0 }: KPICardProps) {
  const isPositive = change && change > 0;

  return (
    <GlassCard padding="lg" delay={delay} className="min-w-[200px]">
      <div className="flex items-start justify-between">
        <div className="space-y-2">
          <p className="text-sm font-medium text-text-secondary">{title}</p>
          <motion.p
            className="text-3xl font-bold text-text-primary tabular-nums"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: delay + 0.2, duration: 0.5 }}
          >
            {value}
          </motion.p>
          {change !== undefined && (
            <div className="flex items-center gap-1">
              <span
                className={cn(
                  'text-xs font-medium',
                  isPositive ? 'text-success' : 'text-error'
                )}
              >
                {isPositive ? '+' : ''}
                {change}%
              </span>
              <span className="text-xs text-text-muted">vs last hour</span>
            </div>
          )}
        </div>
        <div
          className={cn(
            'w-11 h-11 rounded-xl bg-gradient-to-br flex items-center justify-center',
            colorMap[color]
          )}
        >
          {icon}
        </div>
      </div>
    </GlassCard>
  );
}
